import { z } from "zod";
import { Email, IsoDate, IsoDateTime, LongText, MxPhone, NonEmptyString, PageQuery, partialWithoutDefaults, Sex, ShortText, Uuid } from "./common.js";
import { AnalyteKind } from "./catalogue.js";
import { CultureResult } from "./culture.js";

// Patients. Sensitive personal data under the LFPDPPP: the API returns them only inside the
// tenant's RLS context and every read of a result is chained (ADR 0011). Nothing here is ever a
// real person in fixtures or tests.

// -- name normalisation ------------------------------------------------------------------------
// The laboratory writes patient names in capitals without accents, as the birth certificate and
// the CURP do: mixed case, accents and stray spaces cause trouble with the civil registry (Very,
// 2026-11-02). Ñ stays: Mexican actas keep it. Every write of first_name, paternal_surname and
// maternal_surname goes through `normalizePatientName` — the command handlers, the people
// importer, the backfill CLI and the web form — so there is exactly one rule.

/** The names a patient record carries; the three fields `normalizePatientName` applies to. */
export const PATIENT_NAME_FIELDS = ["first_name", "paternal_surname", "maternal_surname"] as const;

/**
 * The stored form of one patient name part: capitals without accents ("Jazmín" → "JAZMIN",
 * "Güemes" → "GUEMES") but with Ñ ("Peña" → "PEÑA"), inner whitespace collapsed to one space,
 * trimmed. Idempotent: normalising a normalised value returns it unchanged.
 */
export function normalizePatientName(raw: string): string {
  return patientNameAsTyped(raw).replace(/\s+/gu, " ").trim();
}

/**
 * What a name field shows while the receptionist types: the same capitals-without-accents as the
 * stored form but without trimming or collapsing, so the space she is about to type survives the
 * keystroke.
 */
export function patientNameAsTyped(raw: string): string {
  // NFC first so a decomposed "n + combining tilde" becomes Ñ and survives; upper-case so ñ → Ñ;
  // then strip every combining mark from the text between the Ñs.
  return raw
    .normalize("NFC")
    .toLocaleUpperCase("es-MX")
    .split("Ñ")
    .map((part) => part.normalize("NFD").replace(/\p{M}/gu, "").normalize("NFC"))
    .join("Ñ");
}

// -- age → birth date -------------------------------------------------------------------------
// Reception sometimes only knows how old the patient is (pilot request R-34). The stored truth
// stays a birth date, so the form derives one: "today minus that age". Pure calendar arithmetic on
// ISO days, no time zone involved: the caller passes the laboratory's today.

export const AGE_UNITS = ["years", "months", "days"] as const;
export type AgeUnit = (typeof AGE_UNITS)[number];

function isoParts(day: string): [number, number, number] {
  const [y, m, d] = day.split("-").map(Number) as [number, number, number];
  return [y, m, d];
}

function isoDay(y: number, m: number, d: number): string {
  return `${String(y).padStart(4, "0")}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

/**
 * The birth date implied by "`value` `unit` old" on `today` (ISO day). Years and months subtract
 * calendar-wise and clamp to the end of a shorter month (29 Feb minus one year is 28 Feb); days
 * subtract exactly.
 */
export function birthDateFromAge(value: number, unit: AgeUnit, today: string): string {
  const [y, m, d] = isoParts(today);
  if (unit === "days") {
    const t = new Date(Date.UTC(y, m - 1, d));
    t.setUTCDate(t.getUTCDate() - value);
    return isoDay(t.getUTCFullYear(), t.getUTCMonth() + 1, t.getUTCDate());
  }
  const totalMonths = y * 12 + (m - 1) - (unit === "years" ? value * 12 : value);
  const by = Math.floor(totalMonths / 12);
  const bm = totalMonths - by * 12 + 1;
  const last = new Date(Date.UTC(by, bm, 0)).getUTCDate();
  return isoDay(by, bm, Math.min(d, last));
}

/**
 * The age of a derived birth date in the unit the clerk would have typed it: whole years from 2
 * years up, whole months from 2 months up, days below that (the same cut-offs as the age text).
 */
export function ageFromBirthDate(birthDate: string, today: string): { value: number; unit: AgeUnit } {
  const [by, bm, bd] = isoParts(birthDate);
  const [ty, tm, td] = isoParts(today);
  const months = Math.max(0, (ty - by) * 12 + (tm - bm) - (td < bd ? 1 : 0));
  if (months >= 24) return { value: Math.floor(months / 12), unit: "years" };
  if (months >= 2) return { value: months, unit: "months" };
  const days = Math.round((Date.UTC(ty, tm - 1, td) - Date.UTC(by, bm - 1, bd)) / 86_400_000);
  return { value: Math.max(0, days), unit: "days" };
}

export const Patient = z.object({
  id: Uuid,
  first_name: NonEmptyString,
  paternal_surname: NonEmptyString,
  maternal_surname: ShortText.nullable(),
  birth_date: IsoDate,
  /** The date was derived from an age the clerk typed, not read from a document: never print it as a known date. */
  birth_date_is_estimated: z.boolean(),
  sex: Sex,
  phone: z.string().nullable(),
  email: Email.nullable(),
  origin: ShortText.nullable(),
  notes: LongText.nullable(),
  created_at: IsoDateTime,
  /** Set when a duplicate was merged into another record. */
  merged_into: Uuid.nullable(),
});
export type Patient = z.infer<typeof Patient>;

export const PatientRegisterPayload = z.object({
  first_name: NonEmptyString,
  paternal_surname: NonEmptyString,
  maternal_surname: ShortText.nullable().default(null),
  birth_date: IsoDate,
  birth_date_is_estimated: z.boolean().default(false),
  sex: Sex,
  phone: MxPhone.nullable().default(null),
  email: Email.nullable().default(null),
  /** "Lugar de procedencia" — asked at the counter (meeting 2026-10-31). */
  origin: ShortText.nullable().default(null),
  notes: LongText.nullable().default(null),
});
export type PatientRegisterPayload = z.infer<typeof PatientRegisterPayload>;

/** A patch: only the fields present are written (omitted fields keep their value, see `partialWithoutDefaults`). */
export const PatientUpdatePayload = partialWithoutDefaults(PatientRegisterPayload).extend({ patient_id: Uuid });
export type PatientUpdatePayload = z.infer<typeof PatientUpdatePayload>;

export const PatientMergePayload = z.object({
  /** The record that survives. */
  keep_patient_id: Uuid,
  /** The duplicate; its orders move to `keep_patient_id` and it is marked merged. */
  merge_patient_id: Uuid,
});
export type PatientMergePayload = z.infer<typeof PatientMergePayload>;

export const PatientListQuery = PageQuery.extend({
  /** Matches name fragments and the phone. */
  q: z.string().trim().max(120).optional(),
});
export type PatientListQuery = z.infer<typeof PatientListQuery>;

/** Compact row for the patient picker and the cached index (no results, ever). */
export const PatientSummary = Patient.pick({
  id: true,
  first_name: true,
  paternal_surname: true,
  maternal_surname: true,
  birth_date: true,
  birth_date_is_estimated: true,
  sex: true,
  phone: true,
});
export type PatientSummary = z.infer<typeof PatientSummary>;

/** Visits, studies and validated results of one patient, newest first. Staff screen (ADR 0011: viewing it is an access event). */
export const PatientHistoryValue = z.object({
  analyte_code: z.string(),
  analyte_name: z.string(),
  /** `document` values are the formatted text of ADR 0019 (HTML subset). */
  kind: AnalyteKind,
  value_numeric: z.number().nullable(),
  value_text: z.string().nullable(),
  culture: CultureResult.nullable(),
  unit: z.string().nullable(),
  flag: z.enum(["N", "L", "H", "A"]).nullable(),
  interval_text: z.string().nullable(),
});

export const PatientHistoryItem = z.object({
  order_item_id: Uuid,
  study_clave: z.string(),
  study_name: z.string(),
  state: z.enum(["pending", "captured", "validated", "issued", "amended", "cancelled"]),
  validated_at: IsoDateTime.nullable(),
  /** Only present once validated; nothing unvalidated leaves the capture screen. */
  values: z.array(PatientHistoryValue),
});

/**
 * The PDF the history's print button opens: the order's newest final or amendment report
 * (the migrated Toronja PDFs are `final`). Partial reports are not offered here.
 */
export const PatientHistoryReport = z.object({
  id: Uuid,
  kind: z.enum(["final", "amendment"]),
  rendered_at: IsoDateTime,
  filename: z.string(),
  /** The order owes money: the PDF answers 402 unless a Químico or above asks for a staff view. */
  held_for_balance: z.boolean(),
});
export type PatientHistoryReport = z.infer<typeof PatientHistoryReport>;

export const PatientHistoryOrder = z.object({
  order_id: Uuid,
  folio: z.string(),
  branch_name: z.string(),
  occurred_at: IsoDateTime,
  state: z.string(),
  doctor_display: z.string(),
  total_cents: z.number().int(),
  balance_cents: z.number().int(),
  /** The order's observaciones (`clinical_notes`). */
  clinical_notes: z.string().nullable(),
  origin: z.enum(["app", "migrated"]),
  /** The previous system's folio, on migrated orders. */
  external_folio: z.string().nullable(),
  latest_report: PatientHistoryReport.nullable(),
  items: z.array(PatientHistoryItem),
});

export const PatientHistory = z.object({ patient: Patient, orders: z.array(PatientHistoryOrder) });
export type PatientHistory = z.infer<typeof PatientHistory>;
